import { Mail, RefreshCw, Trash2, Upload } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DataTable } from '../../../../components/DataTable';
import type { Column } from '../../../../components/DataTable';
import { supabase } from '../../../../lib/supabase';

import OutreachDeleteModal, { type DeleteMode } from './OutreachDeleteModal';
import OutreachImportModal from './OutreachImportModal';
import {
  backfillOutreachFromAttributes,
  fetchOutreachDatabaseCounts,
  fetchOutreachEmailLog,
  fetchOutreachEmailStats,
  fetchOutreachKpi,
  fetchOutreachPersonLastEmails,
  fetchOutreachPersonsPage,
  type OutreachStateFilter,
  sendOutreachIntroEmails,
  wipeAllOutreachData,
  type OutreachDatabaseCounts,
  type OutreachEmailLogRow,
  type OutreachEmailStats,
  type OutreachKpi,
  type OutreachPersonLastEmail,
  type OutreachPersonRow,
} from './outreachImportService';
import {
  EMAIL_STATUS_LABEL,
  emailStatusChipClass,
  formatRelativeSent,
  outreachSendBlockReason,
} from './outreachEmailUtils';
import {
  getPersonDisplayName,
  getPersonFunctionalArea,
  getPersonLocation,
} from './outreachPersonUtils';
import {
  CLOSED_SEND_MODAL,
  OutreachSendEmailModal,
  runOutreachSendWithModal,
  type OutreachSendModalState,
} from './OutreachSendEmailModal';

interface OutreachTabSectionProps {
  isActive: boolean;
}

const STATE_LABEL: Record<string, string> = {
  not_contacted: 'New',
  queued: 'Queued',
  in_sequence: 'Emailed',
  responded: 'Responded',
  converted: 'Converted',
  do_not_contact: 'Blocked',
  bounced: 'Bounced',
  lost: 'Lost',
};

type OutreachSubview = 'contacts' | 'emails';

type EmailColumnFilter = 'all' | 'gmail' | 'has_email' | 'no_email' | 'bounced';

const FETCH_TIMEOUT_MS = 60_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error('Request timed out. Click Refresh to try again.')), ms);
    }),
  ]);
}

export function OutreachTabSection({ isActive }: OutreachTabSectionProps) {
  const [kpi, setKpi]                   = useState<OutreachKpi | null>(null);
  const [persons, setPersons]           = useState<OutreachPersonRow[]>([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [showImport, setShowImport]     = useState(false);
  const [selectedIds, setSelectedIds]   = useState<Set<string>>(new Set());
  const [sendModal, setSendModal]       = useState<OutreachSendModalState>(CLOSED_SEND_MODAL);
  const [actionMsg, setActionMsg]       = useState<string | null>(null);
  const [dbCounts, setDbCounts]         = useState<OutreachDatabaseCounts | null>(null);
  const [deleteMode, setDeleteMode]     = useState<DeleteMode | null>(null);
  const [subview, setSubview]           = useState<OutreachSubview>('contacts');
  const [emailStats, setEmailStats]     = useState<OutreachEmailStats | null>(null);
  const [emailLog, setEmailLog]         = useState<OutreachEmailLogRow[]>([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [lastEmailByPerson, setLastEmailByPerson] = useState<Map<string, OutreachPersonLastEmail>>(new Map());
  const [emailStatusFilter, setEmailStatusFilter] = useState<string>('all');
  const [emailColumnFilter, setEmailColumnFilter] = useState<EmailColumnFilter>('all');
  const [stateFilter, setStateFilter] = useState<OutreachStateFilter>('new');
  const [pagedPersonRows, setPagedPersonRows] = useState<OutreachPersonRow[]>([]);
  // ── server-side pagination ──────────────────────────────────────────────
  const [page, setPage]                 = useState(1);
  const [pageSize, setPageSize]         = useState(50);
  const [totalPersons, setTotalPersons] = useState(0);
  const hasLoadedRef = useRef(false);
  const metaLoadedRef = useRef(false);
  const emailLogLoadedRef = useRef(false);
  const loadGenRef = useRef(0);
  const personsRef = useRef(persons);
  personsRef.current = persons;
  const emailLogRef = useRef(emailLog);
  emailLogRef.current = emailLog;

  const ensureSession = useCallback(async () => {
    for (let attempt = 0; attempt < 4; attempt++) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) return session;
      if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
    }
    return null;
  }, []);

  /** Fetch ONLY the current page of contacts (+ exact total + this page's last-email status). */
  const loadPage = useCallback(async (opts?: { silent?: boolean }) => {
    const gen = ++loadGenRef.current;
    if (!opts?.silent && personsRef.current.length === 0) setLoading(true);
    setError(null);
    try {
      const session = await ensureSession();
      if (gen !== loadGenRef.current) return;
      if (!session) {
        setError('Not signed in — please refresh the page or sign in again.');
        return;
      }

      const { rows, total } = await withTimeout(
        fetchOutreachPersonsPage({ page, pageSize, emailFilter: emailColumnFilter, stateFilter }),
        FETCH_TIMEOUT_MS,
      );
      if (gen !== loadGenRef.current) return;
      setPersons(rows);
      setTotalPersons(total);
      hasLoadedRef.current = true;

      const lastEmails = await fetchOutreachPersonLastEmails(rows.map(r => r.id)).catch(
        () => new Map<string, OutreachPersonLastEmail>(),
      );
      if (gen !== loadGenRef.current) return;
      setLastEmailByPerson(lastEmails);
    } catch (e) {
      if (gen !== loadGenRef.current) return;
      // Keep the last good page on screen — never blank the table on a failed
      // fetch. This is what stopped the "results come and go" flicker.
      setError(
        personsRef.current.length > 0
          ? 'Could not refresh — showing last loaded page. Click Refresh to retry.'
          : (e instanceof Error ? e.message : 'Could not load outreach data.'),
      );
    } finally {
      if (gen === loadGenRef.current) setLoading(false);
    }
  }, [page, pageSize, emailColumnFilter, stateFilter, ensureSession]);

  /** Fetch page-independent aggregates (KPIs, counts, email stats) — runs rarely. */
  const loadMeta = useCallback(async (opts?: { runBackfill?: boolean }) => {
    try {
      const session = await ensureSession();
      if (!session) return;
      if (opts?.runBackfill) {
        await withTimeout(backfillOutreachFromAttributes().catch(() => undefined), FETCH_TIMEOUT_MS);
      }
      const [k, counts, stats] = await withTimeout(
        Promise.all([
          fetchOutreachKpi().catch(() => null),
          fetchOutreachDatabaseCounts().catch(() => null),
          fetchOutreachEmailStats().catch(() => null),
        ]),
        FETCH_TIMEOUT_MS,
      ).catch(() => [null, null, null] as const);
      setKpi(k);
      setDbCounts(counts);
      setEmailStats(stats);
      metaLoadedRef.current = true;
    } catch {
      /* meta is best-effort; page load surfaces the primary error */
    }
  }, [ensureSession]);

  // Re-fetch the page whenever the tab is active and page / size / filter change.
  useEffect(() => {
    if (!isActive) return;
    void loadPage({ silent: personsRef.current.length > 0 });
  }, [isActive, loadPage]);

  // Load aggregates once when the tab first becomes active.
  useEffect(() => {
    if (!isActive || metaLoadedRef.current) return;
    void loadMeta();
  }, [isActive, loadMeta]);

  const loadEmailLog = useCallback(async (silent = false) => {
    if (!silent && emailLogRef.current.length === 0) setEmailLoading(true);
    try {
      const [log, stats] = await withTimeout(
        Promise.all([
          fetchOutreachEmailLog(300),
          fetchOutreachEmailStats(),
        ]),
        FETCH_TIMEOUT_MS,
      );
      setEmailLog(log);
      setEmailStats(stats);
      emailLogLoadedRef.current = true;
    } catch (e) {
      emailLogLoadedRef.current = false;
      setError(e instanceof Error ? e.message : 'Could not load email log.');
    } finally {
      setEmailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isActive || subview !== 'emails') return;
    if (emailLogLoadedRef.current) return;
    void loadEmailLog(true);
  }, [isActive, subview, loadEmailLog]);

  const refreshAll = () => {
    emailLogLoadedRef.current = false;
    metaLoadedRef.current = false;
    void loadPage({ silent: persons.length > 0 });
    void loadMeta();
    if (subview === 'emails') {
      void loadEmailLog(true);
    }
  };

  const handlePagedPersonRows = useCallback((rows: OutreachPersonRow[]) => {
    setPagedPersonRows(rows);
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllOnPage = useCallback(() => {
    setSelectedIds(new Set(pagedPersonRows.map(p => p.id)));
  }, [pagedPersonRows]);

  const pageAllSelected = pagedPersonRows.length > 0
    && pagedPersonRows.every(p => selectedIds.has(p.id));
  const pageSomeSelected = pagedPersonRows.some(p => selectedIds.has(p.id)) && !pageAllSelected;

  const clearSel = useCallback(() => setSelectedIds(new Set()), []);

  const handleDeleted = (deleted: number, cancelled: boolean) => {
    setSelectedIds(new Set());
    if (cancelled) {
      setActionMsg(`Delete stopped — ${deleted.toLocaleString()} record(s) already removed.`);
    } else {
      setActionMsg(`Deleted ${deleted.toLocaleString()} record(s).`);
    }
    setPage(1);
    void loadPage({ silent: persons.length > 0 });
    void loadMeta();
  };


  const reloadAfterImport = () => {
    setPage(1);
    void loadPage({ silent: persons.length > 0 });
    void loadMeta({ runBackfill: true });
  };

  const sendToSelected = async () => {
    const selected = persons.filter(p => selectedIds.has(p.id));
    const sendable: string[] = [];
    let skippedInvalid = 0;
    let skippedBounced = 0;

    for (const p of selected) {
      const last = lastEmailByPerson.get(p.id)?.last_email_status ?? null;
      const block = outreachSendBlockReason(p, last);
      if (block === 'bounced' || block === 'blocked' || block === 'converted') {
        skippedBounced++;
        continue;
      }
      if (block) {
        skippedInvalid++;
        continue;
      }
      sendable.push(p.id);
    }

    const batch = sendable.slice(0, 50);
    if (batch.length === 0) {
      setActionMsg(
        skippedInvalid + skippedBounced > 0
          ? `No sendable contacts — ${skippedInvalid} invalid, ${skippedBounced} blocked/bounced.`
          : 'No reachable selected persons with a valid email.',
      );
      return;
    }

    setActionMsg(null);
    const result = await runOutreachSendWithModal(
      batch.length,
      setSendModal,
      () => sendOutreachIntroEmails(batch),
      { confirm: `Send intro email to ${batch.length} person(s)? (Max 50 per batch)` },
    );
    if (!result?.ok) return;

    const skipNote = skippedInvalid + skippedBounced > 0
      ? ` Skipped ${skippedInvalid + skippedBounced} invalid/bounced.`
      : '';
    setActionMsg(
      `Sent ${result.sent ?? batch.length} email(s).${skipNote}`,
    );
    emailLogLoadedRef.current = false;
    void loadPage({ silent: true });
    void loadMeta();
    if (subview === 'emails') void loadEmailLog(true);
  };

  const sending = sendModal.open && sendModal.phase === 'sending';

  const cols = useMemo<Column<OutreachPersonRow>[]>(() => [
    {
      key: 'full_name',
      header: 'Name',
      minWidth: 220,
      alwaysVisible: true,
      cell: (_v, row) => {
        const display = getPersonDisplayName(row);
        return (
          <span className="flex items-center gap-2.5" onClick={e => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedIds.has(row.id)}
              onChange={() => toggleSelect(row.id)}
              onClick={e => e.stopPropagation()}
              className="shrink-0 w-3.5 h-3.5 rounded accent-purple-600 cursor-pointer touch-manipulation"
              aria-label={`Select ${display}`}
            />
            <span className="font-medium text-[13px] truncate" style={{ color: 'var(--vscode-text-primary)' }}>
              {display}
            </span>
          </span>
        );
      },
    },
    {
      key: 'email',
      header: 'Email Id',
      minWidth: 200,
      cell: (_v, row) => (
        <span className="truncate" style={{ color: row.email ? 'var(--vscode-text-primary)' : '#ea580c' }}>
          {row.email ?? 'No email'}
        </span>
      ),
    },
    {
      key: 'phone',
      header: 'Mobile No.',
      minWidth: 120,
      cell: (_v, row) => row.phone ?? '—',
    },
    {
      key: 'functional_area',
      header: 'Functional Area',
      minWidth: 140,
      cell: (_v, row) => getPersonFunctionalArea(row),
    },
    {
      key: 'location',
      header: 'Current Location',
      minWidth: 140,
      cell: (_v, row) => getPersonLocation(row),
    },
    {
      key: 'source_row_status',
      header: 'Status',
      minWidth: 90,
      cell: (_v, row) => row.source_row_status ?? '—',
    },
    {
      key: 'outreach_state',
      header: 'Outreach',
      minWidth: 100,
      cell: (_v, row) => (
        <span className={`ds-cp-meta-chip text-[10px] ${row.outreach_state === 'converted' ? 'ds-cp-meta-chip--success' : ''}`}>
          {STATE_LABEL[row.outreach_state] ?? row.outreach_state}
        </span>
      ),
    },
    {
      key: 'last_emailed_at',
      header: 'Last outreached',
      minWidth: 140,
      cell: (_v, row) => (
        <span className="text-[12px] tabular-nums whitespace-nowrap" style={{ color: 'var(--vscode-text-secondary)' }}>
          {formatRelativeSent(row.last_emailed_at)}
        </span>
      ),
    },
    {
      key: 'last_email_status',
      header: 'Email status',
      minWidth: 110,
      cell: (_v, row) => {
        const last = lastEmailByPerson.get(row.id);
        if (!last) return <span className="text-[12px]" style={{ color: 'var(--vscode-text-tertiary)' }}>—</span>;
        return (
          <span className={`ds-cp-meta-chip text-[10px] ${emailStatusChipClass(last.last_email_status)}`}>
            {EMAIL_STATUS_LABEL[last.last_email_status] ?? last.last_email_status}
          </span>
        );
      },
    },
    {
      key: 'seniority_level',
      header: 'Level',
      minWidth: 80,
      defaultHidden: true,
      cell: (_v, row) => row.seniority_level ?? '—',
    },
    {
      key: 'industry',
      header: 'Industry',
      minWidth: 120,
      defaultHidden: true,
      cell: (_v, row) => row.industry ?? '—',
    },
  ], [selectedIds, toggleSelect, lastEmailByPerson]);

  const filteredEmailLog = useMemo(() => {
    if (emailStatusFilter === 'all') return emailLog;
    return emailLog.filter(r => r.delivery_status === emailStatusFilter);
  }, [emailLog, emailStatusFilter]);

  const emailCols = useMemo<Column<OutreachEmailLogRow>[]>(() => [
    {
      key: 'email',
      header: 'To',
      minWidth: 220,
      alwaysVisible: true,
      cell: (_v, row) => (
        <span className="truncate font-medium text-[13px]" style={{ color: 'var(--vscode-text-primary)' }}>
          {row.email ?? '—'}
        </span>
      ),
    },
    {
      key: 'delivery_status',
      header: 'Status',
      minWidth: 110,
      cell: (_v, row) => (
        <span className={`ds-cp-meta-chip text-[10px] ${emailStatusChipClass(row.delivery_status)}`}>
          {EMAIL_STATUS_LABEL[row.delivery_status] ?? row.delivery_status}
        </span>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      minWidth: 280,
      cell: (_v, row) => (
        <span className="truncate text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
          {row.subject ?? '—'}
        </span>
      ),
    },
    {
      key: 'sent_at',
      header: 'Sent',
      minWidth: 130,
      cell: (_v, row) => (
        <span className="text-[12px] tabular-nums whitespace-nowrap" style={{ color: 'var(--vscode-text-tertiary)' }}>
          {formatRelativeSent(row.sent_at ?? row.created_at)}
        </span>
      ),
    },
    {
      key: 'full_name',
      header: 'Name',
      minWidth: 160,
      defaultHidden: true,
      cell: (_v, row) => getPersonDisplayName(row),
    },
  ], []);

  const totalLabel = (emailColumnFilter === 'all' && stateFilter === 'all')
    ? (dbCounts?.persons ?? kpi?.total_persons ?? totalPersons)
    : totalPersons;
  const showingLabel = totalLabel > 0
    ? `${((page - 1) * pageSize + 1).toLocaleString()}–${Math.min(page * pageSize, totalLabel).toLocaleString()} of ${totalLabel.toLocaleString()}`
    : '0';

  return (
    <>
      <div className="flex gap-1 mb-3 border-b" style={{ borderColor: 'var(--vscode-border-secondary)' }}>
        {([
          { id: 'contacts' as const, label: 'Contacts' },
          { id: 'emails' as const, label: 'Sent emails' },
        ]).map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSubview(tab.id)}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              subview === tab.id ? 'border-purple-600 text-purple-600' : 'border-transparent'
            }`}
            style={{ color: subview === tab.id ? undefined : 'var(--vscode-text-secondary)' }}
          >
            {tab.label}
            {tab.id === 'emails' && (emailStats?.total_emails ?? 0) > 0 && (
              <span className="ml-1.5 text-[10px] opacity-70">({emailStats?.total_emails})</span>
            )}
          </button>
        ))}
      </div>

      {subview === 'contacts' ? (
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
        {[
          { label: 'Total', value: kpi?.total_persons ?? dbCounts?.persons ?? persons.length },
          { label: 'With email', value: kpi?.with_email ?? dbCounts?.with_email ?? 0 },
          { label: 'Reachable', value: kpi?.reachable ?? 0 },
          { label: 'Ready today', value: kpi?.ready_to_send_today ?? 0 },
          { label: 'Converted', value: kpi?.converted ?? 0 },
          { label: 'Blocked', value: kpi?.blocked ?? 0 },
          { label: 'Lost', value: kpi?.lost ?? 0 },
        ].map(item => (
          <div
            key={item.label}
            className="rounded-xl px-3 py-2.5"
            style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-secondary)' }}
          >
            <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>{item.label}</p>
            <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--vscode-text-primary)' }}>{item.value}</p>
          </div>
        ))}
      </div>
      ) : (
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
        {[
          { label: 'Total sent', value: emailStats?.total_emails ?? 0 },
          { label: 'Delivered', value: emailStats?.delivered ?? 0 },
          { label: 'Engaged', value: emailStats?.engaged ?? 0 },
          { label: 'Bounced', value: emailStats?.bounced ?? 0 },
          { label: 'Failed', value: emailStats?.failed ?? 0 },
          { label: 'Pending', value: emailStats?.pending ?? 0 },
          { label: 'Accepted', value: emailStats?.accepted ?? 0 },
        ].map(item => (
          <div
            key={item.label}
            className="rounded-xl px-3 py-2.5"
            style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-secondary)' }}
          >
            <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>{item.label}</p>
            <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--vscode-text-primary)' }}>{item.value}</p>
          </div>
        ))}
      </div>
      )}

      {actionMsg && (
        <p className="text-[12px] mb-2 px-3 py-2 rounded-lg"
           style={{ background: 'var(--vscode-bg-secondary)', color: 'var(--vscode-text-secondary)' }}>
          {actionMsg}
        </p>
      )}

      {error && (
        <p className="text-[12px] mb-2 px-3 py-2 rounded-lg"
           style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c' }}>
          {error}
        </p>
      )}

      {subview === 'contacts' && (
      <>
      <div className="flex flex-wrap items-center gap-1.5 mb-2" role="tablist" aria-label="Filter by outreach status">
        {([
          { id: 'all',       label: 'All' },
          { id: 'new',       label: 'New' },
          { id: 'emailed',   label: 'Emailed' },
          { id: 'responded', label: 'Responded' },
          { id: 'converted', label: 'Converted' },
          { id: 'bounced',   label: 'Bounced' },
          { id: 'blocked',   label: 'Blocked' },
          { id: 'lost',      label: 'Lost' },
        ] as { id: OutreachStateFilter; label: string }[]).map(seg => {
          const active = stateFilter === seg.id;
          return (
            <button
              key={seg.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                if (seg.id === stateFilter) return;
                setStateFilter(seg.id);
                setPage(1);
                clearSel();
              }}
              className="px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors touch-manipulation"
              style={{
                background: active ? 'var(--vscode-brand, #7c3aed)' : 'var(--vscode-bg-secondary)',
                color: active ? '#fff' : 'var(--vscode-text-secondary)',
                border: `1px solid ${active ? 'transparent' : 'var(--vscode-border-secondary)'}`,
              }}
            >
              {seg.label}
            </button>
          );
        })}
      </div>
      <div className="ds-cp-toolbar flex flex-wrap items-center gap-0 mb-0">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 cursor-pointer select-none pl-1 touch-manipulation">
            <input
              type="checkbox"
              checked={pageAllSelected}
              ref={el => { if (el) el.indeterminate = pageSomeSelected; }}
              onChange={() => (pageAllSelected ? clearSel() : selectAllOnPage())}
              className="w-3.5 h-3.5 rounded accent-purple-600"
              aria-label="Select all on this page"
            />
          </label>
          <span className="ds-cp-toolbar-count">{showingLabel} persons</span>
          <select
            value={emailColumnFilter}
            onChange={e => {
              setEmailColumnFilter(e.target.value as EmailColumnFilter);
              setPage(1);
              clearSel();
            }}
            className="rounded-lg px-2 py-1.5 text-[12px] min-h-[36px]"
            style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
            title="Filter by email"
          >
            <option value="all">All emails</option>
            <option value="gmail">Gmail only</option>
            <option value="has_email">Has email</option>
            <option value="no_email">Missing email</option>
            <option value="bounced">Bounced</option>
          </select>
          {dbCounts && (
            <span className="ds-cp-meta-chip text-[10px]">
              {dbCounts.with_email.toLocaleString()} emails
            </span>
          )}
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1 flex-wrap">
          <button
            type="button"
            disabled={(dbCounts?.persons ?? 0) === 0}
            onClick={() => setDeleteMode('batch')}
            className="exa-toolbar-btn touch-manipulation min-h-[44px]"
            title="Delete by import batch"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">By batch</span>
          </button>
          <button
            type="button"
            disabled={(dbCounts?.persons ?? 0) === 0}
            onClick={() => setDeleteMode('all')}
            className="exa-toolbar-btn exa-toolbar-btn--danger touch-manipulation min-h-[44px]"
            title="Delete all outreach records"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Delete all</span>
          </button>
          <span className="ds-cp-toolbar-sep" aria-hidden />
          {selectedIds.size > 0 && (
            <>
              <button
                type="button"
                disabled={sending}
                onClick={() => void sendToSelected()}
                className="exa-toolbar-btn exa-toolbar-btn--success touch-manipulation min-h-[44px]"
                title="Send intro email (max 50)"
              >
                <Mail className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Send email</span>
                ({selectedIds.size})
              </button>
              <button
                type="button"
                onClick={() => setDeleteMode('selected')}
                className="exa-toolbar-btn exa-toolbar-btn--danger touch-manipulation min-h-[44px]"
                title="Delete selected"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Delete</span>
              </button>
              <span className="ds-cp-toolbar-sep" aria-hidden />
            </>
          )}
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="exa-toolbar-btn exa-toolbar-btn-accent touch-manipulation min-h-[44px]"
          >
            <Upload className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Import CSV / Excel</span>
          </button>
          <button
            type="button"
            onClick={refreshAll}
            disabled={loading}
            className="exa-toolbar-btn touch-manipulation min-h-[44px] min-w-[44px]"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <DataTable<OutreachPersonRow>
          columns={cols}
          rows={persons}
          loading={loading}
          onPagedRowsChange={handlePagedPersonRows}
          serverPagination
          totalRows={totalLabel}
          page={page}
          onPageChange={(p) => { setPage(p); clearSel(); }}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); clearSel(); }}
          pageSizeOptions={[20, 50, 100, 200]}
          initialPageSize={50}
          reserveFloatingSpace
          ariaLabel="Outreach persons"
          emptyMessage={
            (emailColumnFilter === 'all' && stateFilter === 'all')
              ? 'No outreach persons yet — import a CSV or Excel file to get started.'
              : 'No contacts match this filter.'
          }
        />
      </>
      )}

      {subview === 'emails' && (
      <>
      <div className="ds-cp-toolbar flex flex-wrap items-center gap-0 mb-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="ds-cp-toolbar-count">{filteredEmailLog.length} emails</span>
          <select
            value={emailStatusFilter}
            onChange={e => setEmailStatusFilter(e.target.value)}
            className="rounded-lg px-2 py-1.5 text-[12px] min-h-[36px]"
            style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
          >
            <option value="all">All statuses</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="bounced">Bounced</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => void loadEmailLog(false)}
          disabled={emailLoading}
          className="exa-toolbar-btn touch-manipulation min-h-[44px] min-w-[44px]"
          title="Refresh email log"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${emailLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <DataTable<OutreachEmailLogRow>
          columns={emailCols}
          rows={filteredEmailLog}
          loading={false}
          pageSizeOptions={[20, 50, 100]}
          initialPageSize={50}
          reserveFloatingSpace
          ariaLabel="Outreach sent emails"
          emptyMessage="No emails sent yet — select contacts and click Send email."
        />
      <p className="text-[11px] mt-2 px-1" style={{ color: 'var(--vscode-text-tertiary)' }}>
        Statuses sync from Resend webhooks (Delivered, Bounced, etc.). Register webhook URL in Resend dashboard.
      </p>
      </>
      )}

      {showImport && (
        <OutreachImportModal
          onClose={() => setShowImport(false)}
          onImported={reloadAfterImport}
        />
      )}

      {deleteMode && (
        <OutreachDeleteModal
          mode={deleteMode}
          selectedIds={[...selectedIds]}
          totalCount={dbCounts?.persons ?? kpi?.total_persons ?? 0}
          onClose={() => setDeleteMode(null)}
          onDeleted={(deleted, cancelled) => {
            setDeleteMode(null);
            handleDeleted(deleted, cancelled);
          }}
          onWipeAll={async () => {
            const result = await wipeAllOutreachData();
            return { ok: result.ok, error: result.error };
          }}
        />
      )}

      <OutreachSendEmailModal
        state={sendModal}
        onClose={() => setSendModal(CLOSED_SEND_MODAL)}
      />
    </>
  );
}

export default OutreachTabSection;
